import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import type { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { BaseDialog } from '@/components/shared/base-dialog';
import { useNotification } from '@/context/notification';
import {
  useAddDependent,
  useUpdateDependent,
} from '@/data/collaborators/dependents';
import {
  KinshipDegree,
  kinshipDegreeLabels,
  type Dependent,
} from '@/data/collaborators/dependents/interfaces';

const formValidationSchema = z.object({
  name: z.string().nonempty(),
  email: z.email().nonempty(),
  kinship_degree: z.string().nonempty(),
});

type FormValues = z.infer<typeof formValidationSchema>;

interface ManageDependentDialogProps {
  dependent?: Dependent;
  onClose: () => void;
}

export const ManageDependentDialog = ({
  onClose,
  dependent,
}: ManageDependentDialogProps) => {
  const { toast } = useNotification();
  const { mutate: updateDependent, isPending: isUpdating } =
    useUpdateDependent();
  const { mutate: addDependent, isPending } = useAddDependent();
  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: dependent?.user.nome || '',
      email: dependent?.user.email || '',
      kinship_degree: dependent?.kinship_degree || KinshipDegree.Spouse,
    },
    resolver: zodResolver(formValidationSchema),
  });

  const isLoading = isUpdating || isPending;

  const handleUpdate = (data: FormValues) => {
    updateDependent(
      {
        id: dependent?.id ?? 0,
        kinship_degree: data.kinship_degree as KinshipDegree,
        name: data.name,
        email: data.email,
      },
      {
        onSuccess: () => {
          toast({
            content: 'As informações do dependente foram atualizadas.',
            title: 'Dependente atualizado',
            type: 'success',
          });
          onClose();
        },
        onError: (error) => {
          toast({
            content:
              (error as AxiosError<{ data: { message: string } }>).response
                ?.data?.data.message || 'Erro ao alterar dependente.',
            title: 'Erro',
            type: 'error',
          });
        },
      }
    );
  };

  const handleAdd = (data: FormValues) => {
    addDependent(
      {
        kinship_degree: data.kinship_degree as KinshipDegree,
        name: data.name,
        email: data.email,
      },
      {
        onSuccess: () => {
          toast({
            content: `Um convite foi enviado para: '${data.email}', com os dados de acesso à plataforma.`,
            title: 'Dependente adicionado',
            type: 'success',
          });
          onClose();
        },
        onError: (error) => {
          toast({
            content:
              (error as AxiosError<{ data: { message: string } }>).response
                ?.data?.data.message || 'Erro ao criar dependente.',
            title: 'Erro',
            type: 'error',
          });
        },
      }
    );
  };

  const onSubmit = (data: FormValues) => {
    if (dependent) {
      handleUpdate(data);
    } else {
      handleAdd(data);
    }
  };

  const title = dependent ? 'Editar dependente' : 'Adicionar dependente';

  return (
    <BaseDialog
      title={title}
      onClose={onClose}
      open
      actionsComponent={
        <>
          <Button onClick={onClose} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            loading={isLoading}
            startIcon={dependent ? <SaveAltIcon /> : <AddIcon />}
            variant="contained"
          >
            {dependent ? 'Salvar alterações' : 'Adicionar dependente'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormLabel id="name-label" htmlFor="name">
              Nome completo
            </FormLabel>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="name"
                  error={!!error}
                  fullWidth
                  placeholder="Insira o nome completo do dependente"
                  variant="outlined"
                  helperText={error?.message || ' '}
                  aria-labelledby="name-label"
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <FormLabel id="email-label" htmlFor="email">
              E-mail
            </FormLabel>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="email"
                  error={!!error}
                  fullWidth
                  type="email"
                  variant="outlined"
                  placeholder="Insira o e-mail do dependente"
                  helperText={error?.message || ' '}
                  aria-labelledby="email-label"
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              control={control}
              name="kinship_degree"
              render={({ field, fieldState: { error } }) => (
                <FormControl component="fieldset" error={!!error}>
                  <FormLabel component="legend" id="kinship_degree-label">
                    Vínculo/Parentesco
                  </FormLabel>
                  <RadioGroup
                    {...field}
                    row
                    aria-labelledby="kinship_degree-label"
                  >
                    {Object.entries(kinshipDegreeLabels).map(([key, value]) => (
                      <FormControlLabel
                        control={<Radio size="small" />}
                        key={key}
                        label={<Typography variant="body2">{value}</Typography>}
                        value={key}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
              rules={{ required: true }}
            />
          </Grid>
        </Grid>
      </form>
    </BaseDialog>
  );
};
